import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import PageHeader from '../../components/common/PageHeader';
import StatsCard from '../../components/common/StatsCard';
import { Activity, DollarSign, TrendingUp, Layers } from 'lucide-react';

type CopiedTrade = {
  id: string;
  symbol: string;
  type: 'BUY' | 'SELL';
  lot: number;
  entry: number;
  currentPrice: number;
  profit: number;
  sl: number;
  tp: number;
  status: 'OPEN' | 'PENDING';
  strategyName: string;
  traderName: string;
};

const MOCK_LIVE_TRADES: CopiedTrade[] = [
  {
    id: 'TRD-101',
    symbol: 'XAUUSD',
    type: 'BUY',
    lot: 0.5,
    entry: 2032.5,
    currentPrice: 2038.1,
    profit: 280.0,
    sl: 2025.0,
    tp: 2050.0,
    status: 'OPEN',
    strategyName: 'Gold Scalper Pro',
    traderName: 'John Smith',
  },
  {
    id: 'TRD-102',
    symbol: 'EURUSD',
    type: 'SELL',
    lot: 1.0,
    entry: 1.0785,
    currentPrice: 1.0762,
    profit: 230.0,
    sl: 1.082,
    tp: 1.071,
    status: 'OPEN',
    strategyName: 'Forex Trend Master',
    traderName: 'Jane Doe',
  },
  {
    id: 'TRD-103',
    symbol: 'GBPUSD',
    type: 'BUY',
    lot: 0.3,
    entry: 1.261,
    currentPrice: 1.2595,
    profit: -45.0,
    sl: 1.255,
    tp: 1.272,
    status: 'OPEN',
    strategyName: 'Conservative Growth',
    traderName: 'Bob Johnson',
  },
];

const LiveCopiedTrades = () => {
  const [trades] = useState<CopiedTrade[]>(MOCK_LIVE_TRADES);
  const [searchTerm, setSearchTerm] = useState('');

  const totalFloatingProfit = trades.reduce((sum, t) => sum + t.profit, 0);
  const totalVolume = trades.reduce((sum, t) => sum + t.lot, 0);

  const columns: DataTableColumn<CopiedTrade>[] = [
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: row => <span className="font-semibold">{row.symbol}</span>,
      sortable: true,
      width: '120px',
    },
    {
      id: 'strategyName',
      header: 'Strategy / Master',
      accessor: row => (
        <div>
          <span className="font-medium text-xs block">{row.strategyName}</span>
          <span className="text-[10px] text-muted-foreground">
            {row.traderName}
          </span>
        </div>
      ),
      sortable: true,
      width: '180px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <Badge
          className={
            row.type === 'BUY'
              ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300'
              : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
          }
        >
          {row.type}
        </Badge>
      ),
      sortable: true,
      width: '100px',
    },
    {
      id: 'lot',
      header: 'Lot Size',
      accessor: row => row.lot,
      sortable: true,
      width: '100px',
    },
    {
      id: 'entry',
      header: 'Entry Price',
      accessor: row => row.entry,
      sortable: true,
      width: '120px',
    },
    {
      id: 'currentPrice',
      header: 'Current Price',
      accessor: row => row.currentPrice,
      sortable: true,
      width: '120px',
    },
    {
      id: 'sl',
      header: 'Stop Loss',
      accessor: row => row.sl || '-',
      sortable: true,
      width: '110px',
    },
    {
      id: 'tp',
      header: 'Take Profit',
      accessor: row => row.tp || '-',
      sortable: true,
      width: '110px',
    },
    {
      id: 'profit',
      header: 'Floating P/L ($)',
      accessor: row => (
        <span
          className={
            row.profit >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          ${row.profit.toFixed(2)}
        </span>
      ),
      sortable: true,
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300">
          {row.status}
        </span>
      ),
      sortable: true,
      width: '100px',
    },
  ];

  const filteredTrades = trades.filter(
    t =>
      searchTerm === '' ||
      t.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.strategyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.traderName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Live Copied Trades"
          description="Monitor open market positions automatically copied from master strategy providers"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Open Positions"
          value={trades.length.toString()}
          change="Active orders"
          icon={Activity}
        />
        <StatsCard
          title="Floating P/L"
          value={`$${totalFloatingProfit.toFixed(2)}`}
          change="Unrealized profit"
          icon={TrendingUp}
        />
        <StatsCard
          title="Total Open Volume"
          value={`${totalVolume.toFixed(2)} Lots`}
          change="Combined position size"
          icon={Layers}
        />
        <StatsCard
          title="Active Margin Used"
          value="$1,420.00"
          change="Margin collateral"
          icon={DollarSign}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Real-Time Open Copied Orders</CardTitle>
          <CardDescription>
            Live view of copied positions synced from master traders
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filteredTrades}
            searchPlaceholder="Search by symbol, strategy, or trader..."
            onSearchChange={setSearchTerm}
            showAddButton={false}
            showExportButton={false}
            showColumnVisibility={true}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveCopiedTrades;
